import {GLOBAL, type GlobalWindow} from "../../../common/globals";
import {Deferred, EOP_ERRORS} from "../../../common/utils/promises";
import {Load} from "../../../common/load";
import {autoRegister, resolve} from "../../../container";
import {Configuration} from "../../../common/config";
import {EopYoutubePlayer} from "./youtubePlayer";
import {EopSocialYoutubeSeo, type VideoInformation} from "./socialYoutubeSeo";
import {VideoProgress} from "../../../tracking/videoTracking";

export type YoutubeWindow = GlobalWindow & {
    onYouTubeIframeAPIReady: () => void;
};

const YOUTUBE_API_URL = "https://www.googleapis.com/youtube/v3/videos";

@autoRegister()
export class YoutubeIframeApiLoader {
    private deferred: Deferred<void>;
    private loaded: boolean;

    private window: YoutubeWindow;

    public constructor(
        private configuration: Configuration = resolve(Configuration),
        private load: Load = resolve(Load)
    ) {
        this.window = GLOBAL.window() as YoutubeWindow;

        this.deferred = new Deferred<void>();
        this.loaded = false;
    }

    public run(): Promise<void> {
        if (this.loaded) {
            return this.deferred.promise;
        }

        const YOUTUBE_IFRAME_API_URL = this.configuration.get("YOUTUBE_IFRAME_API_URL");

        if (YOUTUBE_IFRAME_API_URL) {
            this.window.onYouTubeIframeAPIReady = () => {
                this.loaded = true;
                this.deferred.resolve();
            };
            this.load.script(YOUTUBE_IFRAME_API_URL).catch(EOP_ERRORS);
        } else {
            this.deferred.reject("Missing configuration for Youtube Iframe API");
        }

        return this.deferred.promise;
    }
}

@autoRegister()
export class YoutubeService {
    private videoInfoCache = new Map<string, Promise<VideoInformation>>();
    private apiKey: string;

    public constructor(private configuration: Configuration = resolve(Configuration)) {
        this.apiKey = this.configuration.get("YOUTUBE_KEY") ?? "";
    }

    public getVideoInfo(videoId: string): Promise<VideoInformation> {
        if (!this.videoInfoCache.has(videoId)) {
            this.videoInfoCache.set(videoId, this.fetchVideoInfo(videoId));
        }
        return this.videoInfoCache.get(videoId)!;
    }

    private async fetchVideoInfo(videoId: string): Promise<VideoInformation> {
        try {
            const response = await fetch(`${YOUTUBE_API_URL}?` + new URLSearchParams({
                id: videoId,
                part: "contentDetails,snippet",
                key: this.apiKey
            }));
            const data = await response.json();
            const videoInformation: VideoInformation = {};
            if (data?.items?.length === 1) {
                const video = data.items[0];
                videoInformation.duration = video.contentDetails?.duration;
                videoInformation.title = video.snippet?.title;
                videoInformation.description = video.snippet?.description;
                videoInformation.uploadDate = video.snippet?.publishedAt;
            }
            return videoInformation;
        } catch (error) {
            EOP_ERRORS(error);
            throw error;
        }
    }
}

export class YoutubeVideoProgress extends VideoProgress {
    public constructor(private player: YT.Player) {
        super();
    }

    public currentTime = (): number => {
        if (this.player.getCurrentTime) {
            return this.player.getCurrentTime();
        } else {
            return 0;
        }
    };

    public duration = (): number => {
        if (this.player.getDuration) {
            return this.player.getDuration();
        } else {
            return 0;
        }
    };
}

customElements.define("eop-youtube-player", EopYoutubePlayer);
customElements.define("eop-social-youtube-seo", EopSocialYoutubeSeo);